import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, Paper, Stack, Tooltip, Typography,
  LinearProgress, Menu, MenuItem, TextField,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  AttachMoney as AmountIcon,
  CalendarToday as CalIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  EmojiEvents as WonIcon,
  StickyNote2 as NoteIcon,
  Task as TaskIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  SwapHoriz as SwapIcon,
  Apartment as OrgIcon,
  Category as LobIcon,
  PlayArrow as SimulateIcon,
  CheckCircle as DoneIcon,
  Schedule as PendingIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { salesApi, SalesActivity, SalesRecord } from '../lib/api/salesApi'
import { getStatusStyle } from './SalesPage'
import { maskPhoneNumber } from '../utils/phoneUtils'
import { dealsApi } from '../lib/api/dealsApi'
import { themeColors } from '../app/layout/theme'
import { SideCard } from '../components/shared/SideCard'
import { RequirementsSideCard } from '../components/shared/RequirementsSideCard'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { InfoRow } from '../components/shared/DetailViewWrappers'
import { CommentItem, CommentInput, CommentsEmptyState } from '../components/shared/NotesSection'
import { EntityDetailTabs } from '../components/shared/EntityDetailTabs'
import AttachmentsSection from '../components/shared/AttachmentsSection'
import { EmailHistorySection } from '../components/shared/EmailHistorySection'
import { SendEmailDrawer, SendEmailData } from '../components/drawers/SendEmailDrawer'
import { emailApi } from '../lib/api/emailApi'
import { callStore } from '../lib/stores/callStore'
import { contactsApi, Contact } from '../lib/api/contactsApi'
import { customersApi } from '../lib/api/customersApi'
import { usersAdminApi, AdminUser } from '../lib/api/usersAdminApi'
import SalesTasksSection from '../components/sales/SalesTasksSection'
import SalesCallsSection from '../components/sales/SalesCallsSection'
import SalesEventsSection from '../components/sales/SalesEventsSection'
import { UniversalActivityTimeline } from '../components/shared/UniversalActivityTimeline'
import { HelpAssigneeSelector } from '../components/shared/HelpAssigneeSelector'
import { usePermission } from '../lib/hooks/usePermission'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime()) || d.getFullYear() <= 1970) return '—'
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtAmount(amount: number | null | undefined, currency = 'INR') {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(amount)
}
function formatOrderValue(val: string | null | undefined, currency = 'INR') {
  if (!val) return '—'
  const parsed = parseFloat(val.replace(/[^0-9.]/g, ''))
  if (!isNaN(parsed) && parsed !== null) {
    return fmtAmount(parsed, currency)
  }
  return val
}
function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}
const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}



// ── Select Email Contact Modal ────────────────────────────────────────────────

function SelectEmailContactModal({
  open, customerId, onClose, onSelect,
}: { open: boolean; customerId: string | null; onClose: () => void; onSelect: (emails: string[]) => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['contacts-for-email', customerId],
    queryFn: () => contactsApi.getAll({ customerId: customerId!, limit: 100 }),
    enabled: open && !!customerId,
  })
  const contacts: Contact[] = data?.items ?? []
  const withEmail = contacts.filter(c => c.email)

  const toggle = (email: string) => {
    setSelected(prev => { const next = new Set(prev); next.has(email) ? next.delete(email) : next.add(email); return next })
  }
  const handleConfirm = () => {
    if (selected.size === 0) { toast.error('Select at least one recipient'); return }
    onSelect([...selected]); setSelected(new Set())
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">Select Recipients</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : withEmail.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <EmailIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No contacts with email addresses found.</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {withEmail.map(c => {
              const isOn = selected.has(c.email!)
              return (
                <Box key={c.id} onClick={() => toggle(c.email!)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '10px', cursor: 'pointer', border: `1.5px solid ${isOn ? themeColors.primary : '#e5e7eb'}`, bgcolor: isOn ? `${themeColors.primary}08` : '#fafafa', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` } }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: isOn ? themeColors.primary : '#9ca3af', flexShrink: 0 }}>
                    {c.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={600} noWrap>{c.name}</Typography>
                    <Typography fontSize="11.5px" color="text.secondary" noWrap>{c.email}</Typography>
                    {c.role && <Typography fontSize="10.5px" color="text.disabled">{c.role}</Typography>}
                  </Box>
                  {isOn && <CheckIcon sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={selected.size === 0 || withEmail.length === 0}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>
          Compose Email
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Select Call Contact Modal ─────────────────────────────────────────────────

function SelectCallContactModal({
  open, customerId, onClose, onSelect,
}: { open: boolean; customerId: string | null; onClose: () => void; onSelect: (contact: Contact) => void }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['contacts-for-call', customerId],
    queryFn: () => contactsApi.getAll({ customerId: customerId!, limit: 100 }),
    enabled: open && !!customerId,
  })
  const contacts: Contact[] = data?.items ?? []
  const withPhone = contacts.filter(c => c.phone)

  const handleConfirm = () => {
    if (!selectedId) { toast.error('Select a contact to call'); return }
    const contact = withPhone.find(c => c.id === selectedId)
    if (contact) onSelect(contact)
    setSelectedId(null)
  }
  const handleClose = () => { setSelectedId(null); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">Select Contact to Call</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : withPhone.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <PhoneIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No contacts with phone numbers found.</Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {withPhone.map(c => {
              const isOn = selectedId === c.id
              return (
                <Box key={c.id} onClick={() => setSelectedId(c.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '10px', cursor: 'pointer', border: `1.5px solid ${isOn ? themeColors.primary : '#e5e7eb'}`, bgcolor: isOn ? `${themeColors.primary}08` : '#fafafa', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` } }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: isOn ? themeColors.primary : '#9ca3af', flexShrink: 0 }}>
                    {c.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize="13px" fontWeight={600} noWrap>{c.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                      <Typography fontSize="11.5px" color="text.secondary" noWrap>{maskPhoneNumber(c.phone)}</Typography>
                    </Box>
                    {c.role && <Typography fontSize="10.5px" color="text.disabled">{c.role}</Typography>}
                  </Box>
                  {isOn && <CheckIcon sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedId || withPhone.length === 0}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>
          Start Call
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Reassign Owner Modal ──────────────────────────────────────────────────────

function ReassignOwnerModal({
  open, currentOwnerId, orgId, onClose, onSelect,
}: { open: boolean; currentOwnerId: string; orgId: string | null; onClose: () => void; onSelect: (user: AdminUser) => void }) {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users-for-assign', orgId ?? 'all'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000, status: 'Active', ...(orgId ? { orgId } : {}) }),
    enabled: open,
    staleTime: 2 * 60_000,
  })
  const users: AdminUser[] = data?.items ?? []
  const filtered = users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase()))

  const handleConfirm = () => {
    const user = users.find(u => u.id === selectedId)
    if (!user) return
    onSelect(user); setSelectedId(null); setSearch('')
  }
  const handleClose = () => { setSelectedId(null); setSearch(''); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        <Typography fontWeight={700} fontSize="15px">Reassign Sale</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.25, py: 0.75, borderRadius: '8px', border: '1.5px solid #e5e7eb', bgcolor: '#fafafa', '&:focus-within': { borderColor: themeColors.primary } }}>
          <PersonIcon sx={{ fontSize: 15, color: '#9ca3af' }} />
          <Box component="input" value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            placeholder="Search by name or email…" autoFocus
            sx={{ flex: 1, border: 'none', outline: 'none', bgcolor: 'transparent', fontSize: '13px', color: 'text.primary' }} />
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <PersonIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No users found</Typography>
          </Box>
        ) : (
          <Stack spacing={0.75} sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {filtered.map(u => {
              const isSelected = selectedId === u.id
              const isCurrent = u.id === currentOwnerId
              return (
                <Box key={u.id} onClick={() => !isCurrent && setSelectedId(u.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '10px', cursor: isCurrent ? 'default' : 'pointer', border: `1.5px solid ${isSelected ? themeColors.primary : isCurrent ? '#bbf7d0' : '#e5e7eb'}`, bgcolor: isSelected ? `${themeColors.primary}08` : isCurrent ? '#f0fdf4' : '#fafafa', transition: 'all 0.15s', '&:hover': !isCurrent ? { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` } : {} }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: isSelected ? themeColors.primary : isCurrent ? '#16a34a' : '#9ca3af', flexShrink: 0 }}>
                    {u.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography fontSize="13px" fontWeight={600} noWrap>{u.name}</Typography>
                      {isCurrent && (
                        <Chip label="Current" size="small" sx={{ height: 16, fontSize: '9.5px', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-label': { px: '5px' } }} />
                      )}
                    </Box>
                    <Typography fontSize="11.5px" color="text.secondary" noWrap>{u.email}</Typography>
                    {u.roleName && <Typography fontSize="10.5px" color="text.disabled" noWrap>{u.roleName}</Typography>}
                  </Box>
                  {isSelected && <CheckIcon sx={{ fontSize: 16, color: themeColors.primary, flexShrink: 0 }} />}
                  {isCurrent && !isSelected && <CheckIcon sx={{ fontSize: 16, color: '#16a34a', flexShrink: 0 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedId}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}>
          Reassign
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Sales Stage Progress Bar ──────────────────────────────────────────────────

function SalesStageProgressBar({
  salesRecord,
  isChanging,
  canUpdatePipeline,
  onSelectStage,
}: {
  salesRecord: SalesRecord
  isChanging?: boolean
  canUpdatePipeline?: boolean
  onSelectStage?: (status: 'New' | 'Onboarded' | 'Won' | 'Lost') => void
}) {
  const status = salesRecord.status || 'New'
  const activeSteps = ['New', 'Onboarded', 'Won']
  
  const currentIdx = activeSteps.indexOf(status)
  const isTerminal = status === 'Won' || status === 'Lost'
  const isWon = status === 'Won'
  const isLost = status === 'Lost'

  let pct = 20
  if (status === 'Onboarded') pct = 60
  else if (status === 'Won' || status === 'Lost') pct = 100

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Typography fontSize="12px" fontWeight={600} color="text.secondary">
          Pipeline Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isChanging && (
            <CircularProgress size={10} sx={{ color: themeColors.primary }} />
          )}
          <Typography fontSize="12px" fontWeight={700} color={isLost ? '#dc2626' : (isWon ? '#16a34a' : themeColors.primary)}>
            {status} ({pct}%)
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: '#e5e7eb',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            bgcolor: isLost
              ? '#ef4444'
              : isWon
                ? '#16a34a'
                : themeColors.primary,
            transition: 'width 0.4s ease',
          },
        }}
      />

      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
        {activeSteps.map((s, i) => {
          const isCurrent = s === status
          const isPast = isTerminal || (!isCurrent && currentIdx >= 0 && i < currentIdx)
          const isFuture = !isTerminal && !isCurrent && currentIdx >= 0 && i > currentIdx

          let color = '#6366f1'
          let bgcolor = '#6366f118'
          let border = '1px solid #6366f144'

          if (s === 'New') {
            color = '#0369a1'
            bgcolor = '#e0f2fe'
            border = '1px solid #bae6fd'
          } else if (s === 'Onboarded') {
            color = '#c2410c'
            bgcolor = '#fff7ed'
            border = '1px solid #ffedd5'
          } else if (s === 'Won') {
            color = '#16a34a'
            bgcolor = '#f0fdf4'
            border = '1px solid #bbf7d0'
          }

          const canClickStep = canUpdatePipeline && !isCurrent && !isChanging

          return (
            <Tooltip
              key={s}
              title={
                isCurrent
                  ? `Current: ${s}`
                  : canClickStep
                    ? `Click to change stage to ${s}`
                    : isPast
                      ? `Completed: ${s}`
                      : s
              }
              arrow
            >
              <Box
                onClick={() => {
                  if (canClickStep && onSelectStage) {
                    onSelectStage(s as 'New' | 'Onboarded' | 'Won')
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 50,
                  textAlign: 'center',
                  py: 0.5,
                  px: 0.5,
                  borderRadius: '4px',
                  position: 'relative',
                  cursor: canClickStep ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  '&:hover': canClickStep ? {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 6px ${color}40`,
                  } : {},
                  ...(isCurrent && {
                    cursor: 'default',
                    bgcolor: bgcolor,
                    border: border,
                    transform: 'scaleY(1.05)',
                    boxShadow: `0 2px 8px ${color}30`,
                  }),
                  ...(isPast && !isCurrent && {
                    bgcolor: '#f0fdf4',
                    border: '1.5px solid #86efac',
                    opacity: 0.75,
                  }),
                  ...(isFuture && {
                    bgcolor: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                  }),
                }}
              >
                <Typography
                  fontSize="9px"
                  fontWeight={isCurrent ? 700 : 500}
                  color={isCurrent ? color : (isPast ? '#16a34a' : 'text.secondary')}
                  noWrap
                >
                  {s}
                </Typography>
              </Box>
            </Tooltip>
          )
        })}
      </Box>
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const SalesDetailPage = observer(function SalesDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const tabParam = new URLSearchParams(window.location.search).get('tab')
  const initialTab = tabParam === 'tasks' ? 4 : 0
  const [activeTab, setActiveTab] = useState(initialTab)
  const [emailOpen, setEmailOpen] = useState(false)
  const [emailTo, setEmailTo] = useState<string[]>([])
  const [contactPickerOpen, setContactPickerOpen] = useState(false)
  const [callPickerOpen, setCallPickerOpen] = useState(false)
  const [headerCreateEventOpen, setHeaderCreateEventOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [confirmLostOpen, setConfirmLostOpen] = useState(false)
  const [confirmWonOpen, setConfirmWonOpen] = useState(false)

  const { hasAction } = usePermission()
  const canManageStage     = hasAction('/sales', 'manage')
  const canAssign          = hasAction('/sales', 'assign')
  const canUpdateRequirements = hasAction('/sales', 'update_requirements') || hasAction('/sales', 'update') || canManageStage

  // ── Privilege action keys ──────────────────────────────────────────────────
  const canCallLog         = hasAction('/sales', 'call_log')
  const canViewCalls       = hasAction('/sales', 'call_history')
  const canCallDownload    = hasAction('/sales', 'call_download')
  const canCallTranscript  = hasAction('/sales', 'call_transcript')
  const canComposeMail     = hasAction('/sales', 'compose_mail')
  const canViewMail        = hasAction('/sales', 'view_mail')
  const canAddComment      = hasAction('/sales', 'add_comment')
  const canViewComments    = hasAction('/sales', 'view_comments')
  const canAddTask         = hasAction('/sales', 'add_task')
  const canViewTasks       = hasAction('/sales', 'view_tasks')
  const canAddEvent        = hasAction('/sales', 'add_event')
  const canViewEvents      = hasAction('/sales', 'view_events')
  const canAddAttachment   = hasAction('/sales', 'add_attachment')
  const canViewAttachments = hasAction('/sales', 'view_attachments')
  const canUpdatePipeline  = hasAction('/sales', 'update_pipeline')
  const canConvert         = hasAction('/sales', 'convert')
  const canUpdateOrder   = hasAction('/sales', 'update_order') || hasAction('/sales', 'update_order_id')

  // ── Load sales record — supports both sale ID and deal ID in the URL parameter
  const { data: salesRecord, isLoading: loadingSales } = useQuery({
    queryKey: ['sales-record', id],
    queryFn: async () => {
      try { return await salesApi.getById(id!) } catch { return await salesApi.getByDealId(id!) }
    },
    enabled: !!id,
    retry: false,
  })

  const isOrderPlaced = salesRecord?.onboard?.orderId != null
  const isWonOrLost = salesRecord?.status === 'Won' || salesRecord?.status === 'Lost' || salesRecord?.deal?.status === 'WON' || salesRecord?.deal?.status === 'LOST'

  const [manualOrderIdOpen, setManualOrderIdOpen] = useState(false)
  const [manualOrderIdInput, setManualOrderIdInput] = useState('')
  const [manualOrderValueInput, setManualOrderValueInput] = useState('')

  const updateOrderIdMut = useMutation({
    mutationFn: ({ orderId, orderValue }: { orderId: string | null; orderValue?: string | null }) =>
      salesApi.updateOrderId(salesRecord!.id, orderId, orderValue),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-record', id] })
      qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord!.id] })
      toast.success('Order details updated successfully')
      setManualOrderIdOpen(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update Order details')
  })

  // Fetch full customer to get orgName / lobName / warehouseName
  const { data: customerDetail } = useQuery({
    queryKey: ['customer-for-sales', salesRecord?.deal?.customerId],
    queryFn: () => customersApi.getById(salesRecord!.deal!.customerId!),
    enabled: !!salesRecord?.deal?.customerId,
    staleTime: 5 * 60_000,
  })

  const { data: contactsForCallData } = useQuery({
    queryKey: ['contacts-for-call', salesRecord?.deal?.customerId],
    queryFn: () => contactsApi.getAll({ customerId: salesRecord!.deal!.customerId!, limit: 100 }),
    enabled: !!salesRecord?.deal?.customerId,
  })

  // ── Status Transition Mutation
  const updateStatusMut = useMutation({
    mutationFn: (status: 'New' | 'Onboarded' | 'Won' | 'Lost') => salesApi.updateSaleStatus(salesRecord!.id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-record', id] })
      qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord!.id] })
      toast.success('Sale status updated successfully')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update status')
  })

  const [simulatingOrderPlacement, setSimulatingOrderPlacement] = useState(false)
  const [orderPlacementIdInput, setOrderPlacementIdInput] = useState('')

  const orderPlacementMutation = useMutation({
    mutationFn: () => salesApi.simulateOrderPlacement(salesRecord!.onboard!.customerUserId!, orderPlacementIdInput.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-record', id] })
      qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord!.id] })
      qc.invalidateQueries({ queryKey: ['onboard-records'] })
      toast.success('Third-party order placement callback simulated successfully!')
      setSimulatingOrderPlacement(false)
      setOrderPlacementIdInput('')
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Failed to simulate order placement.')
    }
  })

  const updateReqsMut = useMutation({
    mutationFn: (requirements: string | null) => salesApi.updateRequirements(salesRecord!.id, requirements),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-record', id] })
      qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord?.id] })
      toast.success('Requirements updated')
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Failed to update requirements.')
    }
  })


  // ── Load sales activities for the timeline
  const { data: activities = [], isLoading: loadingActs } = useQuery({
    queryKey: ['sales-activities', salesRecord?.id],
    queryFn: () => salesApi.getActivities(salesRecord!.id),
    enabled: !!salesRecord?.id,
    staleTime: 30_000,
  })

  const { data: calls = [] } = useQuery({
    queryKey: ['sales-calls', salesRecord?.id],
    queryFn: () => salesApi.getCalls(salesRecord!.id),
    enabled: !!salesRecord?.id,
  })

  const { data: emails = [] } = useQuery({
    queryKey: ['emails', 'DEAL', salesRecord?.dealId],
    queryFn: () => emailApi.getByEntity('DEAL', salesRecord!.dealId!),
    enabled: !!salesRecord?.dealId,
  })

  const { data: events = [] } = useQuery({
    queryKey: ['sales-follow-ups', salesRecord?.id],
    queryFn: () => salesApi.getFollowUps(salesRecord!.id),
    enabled: !!salesRecord?.id,
  })

  const { data: tasks = [] } = useQuery({
    queryKey: ['sales-tasks', salesRecord?.id],
    queryFn: () => salesApi.getTasks(salesRecord!.id),
    enabled: !!salesRecord?.id,
  })

  // ── Notes
  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ['sales-notes', salesRecord?.id],
    queryFn: () => salesApi.getNotes(salesRecord!.id),
    enabled: !!salesRecord?.id,
  })

  const addNoteMut = useMutation({
    mutationFn: (content: string) => salesApi.addNote(salesRecord!.id, content),
    onSuccess: newNotes => {
      qc.setQueryData(['sales-notes', salesRecord!.id], newNotes)
      qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord!.id] })
      toast.success('Note added')
    },
  })
  const updateNoteMut = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) =>
      salesApi.updateNote(salesRecord!.id, noteId, content),
    onSuccess: newNotes => {
      qc.setQueryData(['sales-notes', salesRecord!.id], newNotes)
      toast.success('Note updated')
    },
  })
  const deleteNoteMut = useMutation({
    mutationFn: (noteId: string) => salesApi.deleteNote(salesRecord!.id, noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-notes', salesRecord!.id] })
      qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord!.id] })
      toast.success('Note deleted')
    },
  })

  // ── Auto-log calls completed via the global callStore dialer
  const lastActiveCallRef = useRef<any>(null)
  const currentPhase = callStore.phase
  const currentActiveCall = callStore.activeCall

  useEffect(() => {
    if (currentPhase === 'OUTCOME' && currentActiveCall) {
      lastActiveCallRef.current = {
        outcome: currentActiveCall.outcome,
        durationSeconds: currentActiveCall.durationSeconds,
        notes: currentActiveCall.notes,
      }
    }
  }, [currentPhase, currentActiveCall])


  // ── Reassign — updates the sales record assignee
  const reassignMut = useMutation({
    mutationFn: (assigneeId: string) => {
      return salesApi.reassign(salesRecord!.id, assigneeId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-record', id] })
      toast.success('Reassigned successfully')
      setReassignOpen(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to reassign'),
  })

  // ── Send email — uses DEAL entity for email infra; also logs to sales_activities
  const sendEmailMut = useMutation({
    mutationFn: (data: SendEmailData) =>
      emailApi.send({
        entityType: 'DEAL',
        entityId: salesRecord?.dealId,
        to: data.to,
        cc: data.cc.length > 0 ? data.cc : undefined,
        bcc: data.bcc.length > 0 ? data.bcc : undefined,
        subject: data.subject,
        htmlBody: data.body,
        files: data.attachments.length > 0 ? data.attachments : undefined,
      }),
    onSuccess: (_, variables) => {
      toast.success('Email sent')
      setEmailOpen(false)
      qc.invalidateQueries({ queryKey: ['emails', 'DEAL', salesRecord?.dealId] })
      if (salesRecord?.id) {
        salesApi.addActivity(salesRecord.id, {
          type: 'EMAIL',
          title: `Email sent: ${variables.subject}`,
          description: `To: ${variables.to.join(', ')}\nSubject: ${variables.subject}`,
        }).then(() => {
          qc.invalidateQueries({ queryKey: ['sales-activities', salesRecord.id] })
        })
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to send email'),
  })

  // ── Loading / error states
  if (loadingSales) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (!salesRecord) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Sales record not found</Typography>
        <Button onClick={() => navigate('/sales')} sx={{ mt: 2 }}>Back to Sales</Button>
      </Box>
    )
  }

  const deal = salesRecord.deal
  const owner = deal?.owner ?? { id: salesRecord.ownerId, name: salesRecord.ownerName }
  const assignee = {
    id: salesRecord.assignedToId || '',
    name: salesRecord.assignedToName || 'Unassigned',
    email: salesRecord.assignedToEmail || ''
  }

  const handleLogCall = () => {
    if (!deal?.customerId) {
      setCallPickerOpen(true)
      return
    }
    const contacts = contactsForCallData?.items ?? []
    const withPhone = contacts.filter((c) => c.phone)
    if (withPhone.length === 1) {
      callStore.showPlaceCall({
        entityType: 'SALES',
        entityId: salesRecord.id,
        name: withPhone[0].name,
        phone: withPhone[0].phone,
        companyName: deal?.customer?.companyName ?? deal?.customer?.name ?? undefined,
      })
    } else {
      setCallPickerOpen(true)
    }
  }

  // ── Tab definitions — each activity type points to its own sales_ table via salesRecord.id
  const salesTabs = [
    {
      label: `Activity${activities.length > 0 ? ` (${activities.length})` : ''}`,
      content: (
        <UniversalActivityTimeline
          logs={activities}
          isLoading={loadingActs}
          entityType="sales"
        />
      ),
    },
    ...(canViewCalls ? [{
      label: `Call${calls.length > 0 ? ` (${calls.length})` : ''}`,
      content: (
        <SalesCallsSection
          salesId={salesRecord.id}
          onLogCall={handleLogCall}
          canModify={deal?.status !== 'LOST' && canCallLog}
        />
      ),
    }] : []),
    ...(canViewMail ? [{
      label: `Email${emails.length > 0 ? ` (${emails.length})` : ''}`,
      content: salesRecord.dealId ? (
        <EmailHistorySection
          entityType="DEAL"
          entityId={salesRecord.dealId}
          canModify={deal?.status !== 'LOST' && canComposeMail}
          onCompose={() => setContactPickerOpen(true)}
        />
      ) : (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <EmailIcon sx={{ fontSize: 36, color: '#d1d5db', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">No deal linked — email history unavailable.</Typography>
        </Box>
      ),
    }] : []),
    ...(canViewComments ? [{
      label: `Comments${notes.length > 0 ? ` (${notes.length})` : ''}`,
      content: (
        <Box>
          {canAddComment && <CommentInput onAdd={async c => { await addNoteMut.mutateAsync(c) }} isAdding={addNoteMut.isPending} />}
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : notes.length === 0 ? (
            <CommentsEmptyState />
          ) : (
            <Stack spacing={0}>
              {notes.map((note, idx) => (
                <CommentItem
                  key={note.id}
                  note={note}
                  onUpdate={canAddComment ? async c => { await updateNoteMut.mutateAsync({ noteId: note.id, content: c }) } : undefined}
                  onDelete={canAddComment ? () => deleteNoteMut.mutate(note.id) : undefined}
                  isUpdating={updateNoteMut.isPending}
                  isLast={idx === notes.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      ),
    }] : []),
    ...(canViewTasks ? [{
      label: `Tasks${tasks.length > 0 ? ` (${tasks.length})` : ''}`,
      content: <SalesTasksSection salesId={salesRecord.id} canModify={deal?.status !== 'LOST' && canAddTask} />,
    }] : []),
    ...(canViewEvents ? [{
      label: `Events${events.length > 0 ? ` (${events.length})` : ''}`,
      content: (
        <SalesEventsSection
          salesId={salesRecord.id}
          canModify={deal?.status !== 'LOST' && canAddEvent}
          externalCreateOpen={headerCreateEventOpen}
          onExternalCreateClose={() => setHeaderCreateEventOpen(false)}
        />
      ),
    }] : []),
    ...(canViewAttachments ? [{
      label: 'Attachments',
      content: <AttachmentsSection moduleType="sales" recordId={salesRecord.id} canModify={deal?.status !== 'LOST' && canAddAttachment} />,
    }] : []),
  ]

  return (
    <Box sx={{
      height: '100%', overflowY: 'auto', bgcolor: '#f5f6fa',
      '&::-webkit-scrollbar': { width: '5px' },
      '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: '4px' },
    }}>

      {/* ── Breadcrumb ── */}
      <Box sx={{ px: 3, pt: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => navigate('/sales')} sx={{ color: '#6b7280', mr: 0.5 }}>
          <BackIcon fontSize="small" />
        </IconButton>
        <Typography fontSize="13px" color="text.secondary"
          sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
          onClick={() => navigate('/sales')}>
          Sales
        </Typography>
        <Typography fontSize="13px" color="text.secondary" sx={{ mx: 0.25 }}>/</Typography>
        <Typography fontSize="13px" fontWeight={600} color="text.primary">{salesRecord.dealName}</Typography>
        <Box sx={{ flex: 1 }} />
      </Box>

      {/* ── Hero Card ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
        <Paper elevation={0} sx={{ border: `2px solid ${themeColors.primary}45`, borderRadius: '14px', p: 2.5, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>

            {/* Avatar */}
            <Avatar sx={{ width: 62, height: 62, bgcolor: '#f0fdf4', color: '#16a34a', flexShrink: 0, boxShadow: '0 4px 12px #16a34a30' }}>
              <WonIcon sx={{ fontSize: 30 }} />
            </Avatar>

            {/* Title + chips */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography fontWeight={700} fontSize="18px" color="text.primary">{salesRecord.dealName}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography fontSize="12.5px" color="text.secondary" fontWeight={600}>
                    Expected: {fmtAmount(salesRecord.value, deal?.currency)}
                  </Typography>
                  <Typography fontSize="12.5px" color={salesRecord.onboard?.orderValue ? '#059669' : 'text.secondary'} fontWeight={700}>
                    • Actual: {formatOrderValue(salesRecord.onboard?.orderValue, deal?.currency)}
                  </Typography>
                </Box>
                <Chip
                  label={salesRecord.status || 'New'}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: '4px',
                    fontSize: '10.5px',
                    fontWeight: 700,
                    bgcolor: getStatusStyle(salesRecord.status || 'New').bg,
                    color: getStatusStyle(salesRecord.status || 'New').color,
                    border: getStatusStyle(salesRecord.status || 'New').border,
                    '& .MuiChip-label': { px: '7px' }
                  }}
                />
                {salesRecord.status === 'Won' && salesRecord.closeDate && (
                  <Chip
                    label={`Won Date: ${fmt(salesRecord.closeDate)}`}
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: '4px',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      bgcolor: '#f0fdf4',
                      color: '#16a34a',
                      border: '1px solid #bbf7d0',
                      '& .MuiChip-label': { px: '7px' }
                    }}
                  />
                )}
                {salesRecord.expiryDate && (
                  <Chip
                    label={`Expiry: ${fmt(salesRecord.expiryDate)}`}
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: '4px',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      bgcolor: '#fee2e2',
                      color: '#ef4444',
                      border: '1px solid #fca5a5',
                      '& .MuiChip-label': { px: '7px' }
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
              {deal?.status !== 'LOST' && (
                <>
                  {canCallLog && (
                    <Button size="small" variant="outlined"
                      startIcon={<PhoneIcon sx={{ fontSize: '14px !important' }} />}
                      disabled={callStore.isStarting}
                      onClick={handleLogCall}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                      {callStore.isStarting ? <CircularProgress size={13} sx={{ mr: 0.5 }} /> : null}
                      Log Call
                    </Button>
                  )}
                  {canAddEvent && (
                    <Button size="small" variant="outlined"
                      startIcon={<CalendarIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => { setActiveTab(5); setHeaderCreateEventOpen(true) }}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                      Schedule Meeting
                    </Button>
                  )}
                  {canComposeMail && (
                    <Button size="small" variant="outlined"
                      startIcon={<EmailIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => setContactPickerOpen(true)}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                      Send Email
                    </Button>
                  )}
                </>
              )}
              {deal?.customerId && (
                <Button size="small" variant="outlined"
                  onClick={() => navigate(`/customers/${deal.customerId}`)}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                  View Company
                </Button>
              )}
              {salesRecord.dealId && (
                <Button size="small" variant="outlined"
                  endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={() => navigate(`/deals/${salesRecord.dealId}`)}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', '&:hover': { borderColor: themeColors.primary, color: themeColors.primary } }}>
                  View Deal
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Two-column body ── */}
      <Box sx={{ display: 'flex', gap: 2.5, p: 3, pt: 2, alignItems: 'flex-start' }}>

        {/* ════ Left Sidebar ════ */}
        <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Stage Progress */}
          <SideCard
            title="Pipeline Stage"
          >
            <SalesStageProgressBar
              salesRecord={salesRecord}
              isChanging={updateStatusMut.isPending}
              canUpdatePipeline={canUpdatePipeline}
              onSelectStage={(newStatus) => {
                if (newStatus === 'Won') {
                  const hasOrderId = salesRecord.onboard != null && !!salesRecord.onboard.orderId?.trim()
                  if (!hasOrderId) {
                    toast.error('An Order ID must be entered before marking a sale as Won.')
                    if (canUpdateOrder) {
                      setManualOrderIdInput(salesRecord.onboard?.orderId ?? '')
                      setManualOrderValueInput(salesRecord.onboard?.orderValue ?? (salesRecord.value ? String(salesRecord.value) : ''))
                      setManualOrderIdOpen(true)
                    }
                    return
                  }
                  setConfirmWonOpen(true)
                  return
                }
                updateStatusMut.mutate(newStatus)
              }}
            />
            {/* Mark Lost button (shown only until order is placed or if update_pipeline is granted) */}
            {(salesRecord.status === 'New' || salesRecord.status === 'Onboarded') && 
             (!salesRecord.onboard || !salesRecord.onboard.orderId || canUpdatePipeline) && (canManageStage || canUpdatePipeline) && (
              <Box sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={updateStatusMut.isPending}
                  onClick={() => setConfirmLostOpen(true)}
                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}
                >
                  Mark Lost
                </Button>
              </Box>
            )}
          </SideCard>

          {/* Onboarding Stage/Status */}
          {salesRecord.onboard && (
            <SideCard
              title="Onboard Stage"
              action={!canUpdateOrder && salesRecord.onboard.customerUserId == null ? (
                <Tooltip title="Click to onboard customer">
                  <IconButton
                    size="small"
                    component="a"
                    href={`${import.meta.env.VITE_SALES_APP_URL}/customer-onboard/add?salesId=${salesRecord.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: themeColors.success,
                      p: 0.5,
                      '&:hover': { bgcolor: `${themeColors.success}12` }
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : undefined}
            >
              <Stack spacing={1.5}>
                {/* Onboarding Status */}
                {(() => {
                  const isOnboardCompleted = salesRecord.onboard!.customerUserId != null || salesRecord.status === 'Onboarded' || salesRecord.status === 'Won'
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography fontSize="12px" fontWeight={600} color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={isOnboardCompleted ? 'Completed' : 'Not Completed'}
                        size="small"
                        sx={{
                          height: 20,
                          borderRadius: '4px',
                          bgcolor: isOnboardCompleted ? '#ecfdf5' : '#fef3c7',
                          color: isOnboardCompleted ? '#059669' : '#d97706',
                          border: `1px solid ${isOnboardCompleted ? '#a7f3d0' : '#fde68a'}`,
                          fontSize: '11px',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: '8px' }
                        }}
                      />
                    </Box>
                  )
                })()}

                {/* Redirect to Sales Page / User ID */}
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={500} sx={{ mb: 0.25 }}>
                    Sales User ID
                  </Typography>
                  {salesRecord.onboard.customerUserId ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', p: 1, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      {/* User ID on the left with copy function */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontSize="12px" fontWeight={600} sx={{ fontFamily: 'monospace', color: '#0f172a' }}>
                          {salesRecord.onboard.customerUserId}
                        </Typography>
                        <Tooltip title="Copy User ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(salesRecord.onboard!.customerUserId!)
                              toast.success('Copied to clipboard!')
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {/* Arrow on the right side for redirection - hidden for custom order update role */}
                      {!canUpdateOrder && (
                        <Tooltip title="Redirect to Sales Page">
                          <IconButton
                            component="a"
                            href={`${import.meta.env.VITE_SALES_APP_URL}/customer/detail/${salesRecord.onboard.customerUserId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{
                              p: 0.25,
                              color: themeColors.primary,
                              '&:hover': {
                                color: '#201047',
                                bgcolor: 'rgba(99, 102, 241, 0.08)'
                              }
                            }}
                          >
                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    <Typography fontSize="12px" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      Pending system update...
                    </Typography>
                  )}
                </Box>

                {/* Order Placement Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                  <Typography fontSize="12px" fontWeight={600} color="text.secondary">
                    Order Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={salesRecord.onboard.orderId != null ? 'Placed' : 'Not Placed'}
                      size="small"
                      sx={{
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: salesRecord.onboard.orderId != null ? '#ecfdf5' : '#f3f4f6',
                        color: salesRecord.onboard.orderId != null ? '#059669' : '#4b5563',
                        border: `1px solid ${salesRecord.onboard.orderId != null ? '#a7f3d0' : '#e5e7eb'}`,
                        fontSize: '11px',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: '8px' }
                      }}
                    />
                  </Box>
                </Box>

                {/* Order ID & Redirect / Manual Edit */}
                <Box>
                  <Typography fontSize="11px" color="text.secondary" fontWeight={500} sx={{ mb: 0.25 }}>
                    Order ID
                  </Typography>
                  {salesRecord.onboard.orderId ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', p: 1, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      {/* Order ID on the left with copy function */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontSize="12px" fontWeight={600} sx={{ fontFamily: 'monospace', color: '#0f172a' }}>
                          {salesRecord.onboard.orderId}
                        </Typography>
                        <Tooltip title="Copy Order ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(salesRecord.onboard!.orderId!)
                              toast.success('Copied to clipboard!')
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {/* Arrow on the right side for redirection - hidden for custom order update role */}
                      {!canUpdateOrder && (
                        <Tooltip title="Redirect to Order Page">
                          <IconButton
                            component="a"
                            href={`${import.meta.env.VITE_SALES_APP_URL}/order-detail/${salesRecord.onboard.orderId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{
                              p: 0.25,
                              color: themeColors.primary,
                              '&:hover': {
                                color: '#201047',
                                bgcolor: 'rgba(99, 102, 241, 0.08)'
                              }
                            }}
                          >
                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    <Typography fontSize="12px" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      {salesRecord.onboard.customerUserId != null 
                        ? 'Waiting for order placement...' 
                        : 'Pending onboarding completion...'}
                    </Typography>
                  )}

                  {/* Manual Order ID action button for update_order permission */}
                  {canUpdateOrder && !isWonOrLost && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setManualOrderIdInput(salesRecord.onboard?.orderId ?? '')
                          setManualOrderValueInput(salesRecord.onboard?.orderValue ?? (salesRecord.value ? String(salesRecord.value) : ''))
                          setManualOrderIdOpen(true)
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '6px',
                          fontSize: '11px',
                          py: 0.25,
                          px: 1,
                          fontWeight: 600,
                          borderColor: themeColors.primary,
                          color: themeColors.primary,
                          '&:hover': { bgcolor: `${themeColors.primary}08` }
                        }}
                      >
                        {salesRecord.onboard?.orderId ? 'Edit Order Details' : 'Set Order Details'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Stack>
            </SideCard>
          )}

          {/* Sale Info */}
          <SideCard title="Sale Info">
            <Stack spacing={0.5}>
              <InfoRow icon={<AmountIcon sx={{ fontSize: 15 }} />} label="Expected Value"
                value={
                  <Typography fontSize="13px" fontWeight={700} color={themeColors.primary}>
                    {fmtAmount(salesRecord.value, deal?.currency)}
                  </Typography>
                } />
              <InfoRow icon={<AmountIcon sx={{ fontSize: 15 }} />} label="Actual Value"
                value={
                  <Typography fontSize="13px" fontWeight={700} color={salesRecord.onboard?.orderValue ? '#059669' : themeColors.text.muted}>
                    {formatOrderValue(salesRecord.onboard?.orderValue, deal?.currency)}
                  </Typography>
                } />
              {salesRecord.status === 'Won' && salesRecord.closeDate && (
                <InfoRow icon={<CalIcon sx={{ fontSize: 15 }} />} label="Won Date" value={fmt(salesRecord.closeDate)} />
              )}
              {salesRecord.expiryDate && (
                <InfoRow icon={<CalIcon sx={{ fontSize: 15 }} />} label="Expiry Date" value={fmt(salesRecord.expiryDate)} />
              )}

              <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Sales Stage"
                value={
                  <Chip
                    label={salesRecord.status || 'New'}
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: '4px',
                      bgcolor: getStatusStyle(salesRecord.status || 'New').bg,
                      color: getStatusStyle(salesRecord.status || 'New').color,
                      border: getStatusStyle(salesRecord.status || 'New').border,
                      fontSize: '11px',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: '8px' }
                    }}
                  />
                } />
              {deal?.priority && (
                <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Priority"
                  value={
                    <Chip label={deal.priority} size="small"
                      sx={{ height: 18, fontSize: '10.5px', fontWeight: 700, borderRadius: '4px', bgcolor: '#f3f4f6', color: '#374151', '& .MuiChip-label': { px: '6px' } }} />
                  } />
              )}
              {deal?.source && (
                <InfoRow icon={<InfoIcon sx={{ fontSize: 15 }} />} label="Source" value={deal.source} />
              )}
              <InfoRow icon={<CalIcon sx={{ fontSize: 15 }} />} label="Created" value={fmt(salesRecord.createdAt)} />
            </Stack>
          </SideCard>

          {/* Company & Contact */}
          {(deal?.customer || deal?.contact) && (
            <SideCard title={deal?.customer ? 'Company' : 'Contact'}>
              {deal?.customer && (
                <Box onClick={() => navigate(`/customers/${deal.customerId}`)}
                  sx={{ p: 1, borderRadius: '10px', border: `1px solid ${themeColors.border}`, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: '#f5f7fb' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                    <Typography fontSize="13px" fontWeight={700} color={themeColors.primary}>
                      {deal.customer.companyName || deal.customer.name}
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: 13, color: themeColors.primary, flexShrink: 0 }} />
                  </Box>
                </Box>
              )}
              {deal?.contact && (
                <Box sx={{ mt: deal?.customer ? 1.5 : 0, pt: deal?.customer ? 1.5 : 0, borderTop: deal?.customer ? '1px solid #f0f0f0' : 'none' }}>
                  {deal?.customer && (
                    <Typography fontSize="11px" color="text.secondary" mb={0.75} fontWeight={500}>Primary Contact</Typography>
                  )}
                  <Box onClick={() => navigate(`/contacts/${deal.contactId}`)}
                    sx={{ p: 1, borderRadius: '10px', border: `1px solid ${themeColors.border}`, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: '#f5f7fb' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 14, color: themeColors.primary }} />
                        <Typography fontSize="12.5px" fontWeight={700} color={themeColors.primary}>{deal.contact.name}</Typography>
                      </Box>
                      <OpenInNewIcon sx={{ fontSize: 13, color: themeColors.primary, flexShrink: 0 }} />
                    </Box>
                    {deal.contact.phone && (
                      <Typography fontSize="11px" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <PhoneIcon sx={{ fontSize: 11 }} /> {maskPhoneNumber(deal.contact.phone)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </SideCard>
          )}

          {/* Assigned To */}
          <SideCard title="Assigned To" action={canAssign ?
            <Tooltip title={isOrderPlaced ? "Cannot reassign sale after order is placed" : "Reassign sale"}>
              <span>
                <IconButton size="small" onClick={() => setReassignOpen(true)}
                  disabled={reassignMut.isPending || isOrderPlaced}
                  sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#f5f7fb' } }}>
                  {reassignMut.isPending ? <CircularProgress size={13} /> : <EditIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </span>
            </Tooltip>
          : undefined}>
            <Box sx={{ p: 1, borderRadius: '10px', border: `1px solid ${themeColors.border}`, bgcolor: '#fafafa', transition: 'all 0.15s', '&:hover': { borderColor: themeColors.primary, bgcolor: '#f5f7fb' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '11px', fontWeight: 700, bgcolor: avatarColor(assignee.name), flexShrink: 0 }}>
                  {initials(assignee.name)}
                </Avatar>
                <Typography fontSize="13px" fontWeight={700} color={themeColors.primary} noWrap>{assignee.name}</Typography>
              </Box>
              {assignee.email && (
                <Typography fontSize="11px" color="text.secondary" sx={{ mt: 0.25, ml: '36px' }} noWrap>
                  {assignee.email}
                </Typography>
              )}
            </Box>
            {canAssign && (
              <Box onClick={() => !isOrderPlaced && setReassignOpen(true)}
                sx={{
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  py: 0.6,
                  borderRadius: '8px',
                  border: isOrderPlaced ? '1.5px dashed #ccc' : `1.5px dashed ${themeColors.primary}55`,
                  cursor: isOrderPlaced ? 'not-allowed' : 'pointer',
                  color: isOrderPlaced ? '#9ca3af' : themeColors.primary,
                  transition: 'all 0.15s',
                  opacity: isOrderPlaced ? 0.6 : 1,
                  '&:hover': isOrderPlaced ? {} : { bgcolor: '#f5f7fb', borderColor: themeColors.primary }
                }}>
                <EditIcon sx={{ fontSize: 12 }} />
                <Typography fontSize="11px" fontWeight={600}>Reassign Sale</Typography>
              </Box>
            )}

            <HelpAssigneeSelector
              currentPath="/sales"
              lobId={salesRecord.lobId}
              currentHelpAssigneeId={typeof salesRecord.helpAssignee === 'object' && salesRecord.helpAssignee !== null ? (salesRecord.helpAssignee as any).id : salesRecord.helpAssigneeId}
              currentHelpAssigneeName={typeof salesRecord.helpAssignee === 'object' && salesRecord.helpAssignee !== null ? (salesRecord.helpAssignee as any).name : (salesRecord.helpAssignee as string)}
              disabled={isWonOrLost || isOrderPlaced}
              onUpdate={async (newId) => {
                await salesApi.updateHelpAssignee(salesRecord.id, newId)
                qc.invalidateQueries({ queryKey: ['sales', id] })
                qc.invalidateQueries({ queryKey: ['sales-activities', id] })
              }}
            />
          </SideCard>

          {deal?.description && (
            <SideCard title="Description">
              <Typography fontSize="12.5px" color="text.primary" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {deal.description}
              </Typography>
            </SideCard>
          )}

          {/* Requirements */}
          <RequirementsSideCard
            value={salesRecord?.requirements || deal?.requirements}
            onSave={(reqs) => updateReqsMut.mutateAsync(reqs)}
            isPending={updateReqsMut.isPending}
            canEdit={!isOrderPlaced && !isWonOrLost && canUpdateRequirements}
          />

          {/* Business / LOB / Warehouse */}
          <SideCard title="Business Details">
            <Stack spacing={0.5}>
              <InfoRow
                icon={<OrgIcon sx={{ fontSize: 15 }} />}
                label="Business"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    color={
                      deal?.orgName || customerDetail?.orgName
                        ? 'text.primary'
                        : 'text.disabled'
                    }
                  >
                    {deal?.orgName || customerDetail?.orgName || '—'}
                  </Typography>
                }
              />
              <InfoRow
                icon={<LobIcon sx={{ fontSize: 15 }} />}
                label="LOB"
                value={
                  <Typography
                    fontSize="13px"
                    fontWeight={600}
                    color={
                      deal?.lobName || customerDetail?.lobName
                        ? 'text.primary'
                        : 'text.disabled'
                    }
                  >
                    {deal?.lobName || customerDetail?.lobName || '—'}
                  </Typography>
                }
              />
            </Stack>
          </SideCard>

          {/* Quick navigation */}
          <SideCard>
            {[
              { icon: <TimelineIcon sx={{ fontSize: 16 }} />, label: 'Activity', count: activities.length, tab: 0 },
              { icon: <PhoneIcon    sx={{ fontSize: 16 }} />, label: 'Call',     count: calls.length, tab: 1 },
              { icon: <NoteIcon     sx={{ fontSize: 16 }} />, label: 'Comments', count: notes.length, tab: 3 },
              { icon: <TaskIcon     sx={{ fontSize: 16 }} />, label: 'Tasks',    count: tasks.length, tab: 4 },
              { icon: <CalendarIcon sx={{ fontSize: 16 }} />, label: 'Events',   count: events.length, tab: 5 },
            ].map(item => (
              <Box key={item.label} onClick={() => setActiveTab(item.tab)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '8px', cursor: 'pointer', '&:hover': { bgcolor: '#f5f7fb' } }}>
                <Box sx={{ color: themeColors.primary }}>{item.icon}</Box>
                <Typography fontSize="12.5px" fontWeight={600} sx={{ flex: 1 }}>{item.label}</Typography>
                {item.count != null && (
                  <Chip
                    label={item.count}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '10px',
                      fontWeight: 700,
                      bgcolor: '#f1f5f9',
                      color: '#64748b',
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                )}
              </Box>
            ))}
          </SideCard>

        </Box>

        {/* ════ Right Panel ════ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EntityDetailTabs
            tabs={salesTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            padding={2.5}
            tabHeight={44}
            tabFontSize="13px"
            tabFontWeight={600}
            tabPaddingX={2}
            borderRadius="12px"
          />
        </Box>

      </Box>

      {/* ── Modals ── */}
      {reassignOpen && (
        <ReassignOwnerModal
          open={reassignOpen}
          currentOwnerId={assignee.id}
          orgId={deal?.orgId ?? salesRecord.orgId}
          onClose={() => setReassignOpen(false)}
          onSelect={user => reassignMut.mutate(user.id)}
        />
      )}

      {/* Order Placement Callback Simulation Dialog */}
      <Dialog
        open={simulatingOrderPlacement}
        onClose={() => setSimulatingOrderPlacement(false)}
        PaperProps={{ sx: { borderRadius: '14px', px: 1, py: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>
          Simulate Order Placement Callback
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              This dialog simulates a third-party platform hitting the POST `/sales/onboard-order` API to notify the CRM that the customer has placed an order.
            </Typography>
            <TextField
              label="Order ID"
              size="small"
              fullWidth
              value={orderPlacementIdInput}
              onChange={(e) => setOrderPlacementIdInput(e.target.value)}
              placeholder="e.g. ORD-98172"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setSimulatingOrderPlacement(false)}
            variant="outlined"
            size="small"
            sx={{ borderRadius: '8px', textTransform: 'none', px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => orderPlacementMutation.mutate()}
            disabled={!orderPlacementIdInput.trim() || orderPlacementMutation.isPending}
            variant="contained"
            size="small"
            sx={{ borderRadius: '8px', textTransform: 'none', px: 2, bgcolor: themeColors.primary, '&:hover': { bgcolor: '#201047' } }}
          >
            {orderPlacementMutation.isPending ? 'Simulating...' : 'Submit Callback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Order Details Dialog */}
      {manualOrderIdOpen && (
        <Dialog
          open={manualOrderIdOpen}
          onClose={() => setManualOrderIdOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '14px' } }}
        >
          <DialogTitle sx={{ pb: 1, fontSize: '16px', fontWeight: 700 }}>
            {salesRecord.onboard?.orderId ? 'Update Order Details' : 'Set Order Details'}
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Typography fontSize="13px" color="text.secondary" sx={{ mb: 2 }}>
              Manually enter or update the Order ID and Actual Order Value for this sales record.
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Order ID"
                value={manualOrderIdInput}
                onChange={(e) => setManualOrderIdInput(e.target.value)}
                placeholder="e.g. ORD-100452"
                autoFocus
              />
              <TextField
                fullWidth
                size="small"
                label="Actual Order Value (₹)"
                value={manualOrderValueInput}
                onChange={(e) => setManualOrderValueInput(e.target.value)}
                placeholder="e.g. 50000"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setManualOrderIdOpen(false)}
              variant="outlined"
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={updateOrderIdMut.isPending}
              onClick={() => updateOrderIdMut.mutate({
                orderId: manualOrderIdInput.trim() || null,
                orderValue: manualOrderValueInput.trim() || null,
              })}
              sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: themeColors.primary }}
            >
              {updateOrderIdMut.isPending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save Details'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <SelectCallContactModal
        open={callPickerOpen}
        customerId={deal?.customerId ?? null}
        onClose={() => setCallPickerOpen(false)}
        onSelect={contact => {
          setCallPickerOpen(false)
          callStore.showPlaceCall({
            entityType: 'SALES',
            entityId: salesRecord.id,
            name: contact.name,
            phone: contact.phone,
            companyName: deal?.customer?.companyName ?? deal?.customer?.name ?? undefined,
          })
        }}
      />

      <SelectEmailContactModal
        open={contactPickerOpen}
        customerId={deal?.customerId ?? null}
        onClose={() => setContactPickerOpen(false)}
        onSelect={emails => {
          setEmailTo(emails)
          setContactPickerOpen(false)
          setEmailOpen(true)
        }}
      />

      <SendEmailDrawer
        open={emailOpen}
        onClose={() => { setEmailOpen(false); setEmailTo([]) }}
        defaultTo={emailTo}
        onSend={data => sendEmailMut.mutate(data)}
        isSending={sendEmailMut.isPending}
        orgId={deal?.orgId ?? salesRecord.orgId}
      />

      <ConfirmDialog
        open={confirmLostOpen}
        title="Confirm Mark Sale as Lost"
        description="Are you sure you want to mark this sale as Lost? This action will update the sales status and cannot be undone."
        confirmLabel="Yes, Mark as Lost"
        confirmColor="error"
        onClose={() => setConfirmLostOpen(false)}
        onConfirm={() => {
          updateStatusMut.mutate('Lost')
          setConfirmLostOpen(false)
        }}
        isConfirming={updateStatusMut.isPending}
      />

      <ConfirmDialog
        open={confirmWonOpen}
        title="Confirm Mark Sale as Won"
        description="Are you sure you want to mark this sale as Won? This action will finalize the sale and mark the associated deal as Won."
        confirmLabel="Yes, Mark as Won"
        confirmColor="success"
        icon={<WonIcon sx={{ fontSize: 20, color: '#16a34a' }} />}
        onClose={() => setConfirmWonOpen(false)}
        onConfirm={() => {
          updateStatusMut.mutate('Won')
          setConfirmWonOpen(false)
        }}
        isConfirming={updateStatusMut.isPending}
      />

    </Box>
  )
})

export default SalesDetailPage
